import classNames from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { SecondaryOrderInstantBookWorkflow } from 'routes/secondaryMarketOrderBlotter/SidePanel/SecondaryOrderInstantBookWorkflow';
import { SecondaryOrderSidePanelFooter } from 'routes/secondaryMarketOrderBlotter/SidePanel/SecondaryOrderSidePanelFooter';
import type { CancellationStatus } from 'state/secondaryMarket/shared/cancellation/cancellation.model';
import { isPropertiesDefined } from 'types/utils.guard';
import type { WorkflowStatus } from 'utils/status';

import { GenericErrorBoundary } from '../../../components/common-form/builder/form/GenericErrorBoundary';
import type { SelectedItem } from '../Blotter/SecondaryOrderBlotter.connect';
import { CloseButton } from './Buttons';
import { SecondaryOrderCancellationWorkflow } from './SecondaryOrderCancellationWorkflow';
import { SecondaryOrderSidePanelDetails } from './SecondaryOrderSidePanelDetails';

interface SecondaryOrderSidePanelProps {
  selected: SelectedItem;
  isSecondaryMarketPostTradeRedirectionAllowed: boolean;
  className: string;
  isSecondaryMarketOrderCancellationAllowed: boolean;
  isSecondaryOrderBookEnabled: boolean;
  orderInstantBookStatus: WorkflowStatus | undefined;
  orderInstantBookError: string | undefined;
  orderCancellationStatus: CancellationStatus | undefined;
  orderCancellationError: string | undefined;
  onClose: () => void;
  onOpenSecondaryMarketPostTrade: (orderId: string, tabName: string) => void;
  onOpenNewOrderTab: (orderId: string) => void;
  onInstantBookWorkflowInitiated: (orderId: string) => void;
  onInstantBookWorkflowConfirmed: (orderId: string) => void;
  onInstantBookWorkflowAborted: (orderId: string) => void;
  onInstantBookWorkflowCompleted: (orderId: string) => void;
  onCancellationWorkflowInitiated: (orderId: string) => void;
  onCancellationWorkflowConfirmed: (orderId: string) => void;
  onCancellationWorkflowAborted: (orderId: string) => void;
  onCancellationWorkflowCompleted: (orderId: string) => void;
}

export const SecondaryOrderSidePanel: FC<SecondaryOrderSidePanelProps> = ({
  className,
  isSecondaryMarketPostTradeRedirectionAllowed,
  selected,
  isSecondaryMarketOrderCancellationAllowed,
  orderInstantBookStatus,
  orderInstantBookError,
  orderCancellationStatus,
  orderCancellationError,
  isSecondaryOrderBookEnabled,
  onClose,
  onOpenSecondaryMarketPostTrade,
  onInstantBookWorkflowInitiated,
  onInstantBookWorkflowConfirmed,
  onInstantBookWorkflowAborted,
  onInstantBookWorkflowCompleted,
  onCancellationWorkflowInitiated,
  onCancellationWorkflowConfirmed,
  onCancellationWorkflowAborted,
  onCancellationWorkflowCompleted,
  onOpenNewOrderTab,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className={classNames('card d-flex flex-column', className)}>
      <div className="card-header">
        <div className="w-100 d-flex flex-row align-items-center justify-content-start">
          <h6>{formatMessage({ id: 'secondaryMarketOrderBlotter.details.title' })}</h6>
          <div className="flex-grow-1 text-end">
            <CloseButton onClicked={onClose} />
          </div>
        </div>
      </div>
      <div className="card-body flex-grow-1 flex-shrink-1 overflow-auto">
        <div className="h-100 ">
          <GenericErrorBoundary key={selected.orderId} context={{ orderId: selected?.orderId }}>
            <SecondaryOrderSidePanelDetails item={selected} />
          </GenericErrorBoundary>
        </div>
      </div>
      <div className="card-footer ">
        {isPropertiesDefined(selected, 'orderType') ? (
          <SecondaryOrderSidePanelFooter
            isSecondaryMarketOrderCancellationAllowed={isSecondaryMarketOrderCancellationAllowed}
            onOpenNewOrder={onOpenNewOrderTab}
            isSecondaryMarketPostTradeRedirectionAllowed={isSecondaryMarketPostTradeRedirectionAllowed}
            isSecondaryOrderBookEnabled={isSecondaryOrderBookEnabled}
            onCancellationWorkflowInitiated={onCancellationWorkflowInitiated}
            onInstantBookWorkflowInitiated={onInstantBookWorkflowInitiated}
            onOpenSecondaryMarketPostTrade={onOpenSecondaryMarketPostTrade}
            orderCancellationStatus={orderCancellationStatus}
            selected={selected}
          />
        ) : undefined}

        <GenericErrorBoundary key={selected.orderId} context={{ orderId: selected?.orderId }}>
          <SecondaryOrderCancellationWorkflow
            orderId={selected.orderId}
            orderType={selected.orderType}
            status={orderCancellationStatus}
            error={orderCancellationError}
            onCancellationWorkflowConfirmed={onCancellationWorkflowConfirmed}
            onCancellationWorkflowAborted={onCancellationWorkflowAborted}
            onCancellationWorkflowCompleted={onCancellationWorkflowCompleted}
          />
          <SecondaryOrderInstantBookWorkflow
            orderId={selected.orderId}
            orderInstantBookStatus={orderInstantBookStatus}
            orderInstantBookError={orderInstantBookError}
            onInstantBookWorkflowConfirmed={onInstantBookWorkflowConfirmed}
            onInstantBookWorkflowAborted={onInstantBookWorkflowAborted}
            onInstantBookWorkflowCompleted={onInstantBookWorkflowCompleted}
          />
        </GenericErrorBoundary>
      </div>
    </div>
  );
};

SecondaryOrderSidePanel.displayName = 'SidePanel';
